// src/pages/TradeHistory.js
import React, { useState, useEffect } from 'react';
import { getOrders, STRATEGY_SYMBOLS, STRATEGY_NAMES, getSymbolStrategy } from '../services/api';
import './TradeHistory.css';

const PAGE_SIZE = 5;

const TradeHistory = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('filled');
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
    const interval = filter === 'all' ? setInterval(loadOrders, 30000) : null;
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [filter]);

  // Reset to page 1 whenever filter or strategy changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, selectedStrategy]);

  const loadOrders = async () => {
    try {
      const data = await getOrders('all', 500);
      setOrders(data.orders);
      setLoading(false);
    } catch (error) {
      console.error('Error loading orders:', error);
      setLoading(false);
    }
  };

  // Group filled orders into completed trades, handling both long (buy→sell) and short (sell→buy).
  const groupIntoTrades = () => {
    const filledOrders = orders
      .filter(o => o.status === 'filled' && o.filled_at)
      .sort((a, b) => new Date(a.filled_at) - new Date(b.filled_at));

    const trades = [];
    const openPositions = {}; // symbol -> opening order

    filledOrders.forEach(order => {
      const symbol = order.symbol;
      const open = openPositions[symbol];

      if (!open) {
        // No open position — this order opens one
        openPositions[symbol] = order;
      } else if (open.side !== order.side) {
        // Opposing side — this closes the open position
        const sellOrder = open.side === 'sell' ? open : order;
        const buyOrder  = open.side === 'buy'  ? open : order;

        // P&L formula works for both directions:
        //   Long:  sell_price > buy_price = profit
        //   Short: sell_price > buy_price = profit (sold high, bought low)
        const pnl = (sellOrder.filled_avg_price - buyOrder.filled_avg_price) * order.filled_qty;
        const pnlPercent = ((sellOrder.filled_avg_price - buyOrder.filled_avg_price) / buyOrder.filled_avg_price) * 100;

        trades.push({
          symbol,
          strategy: getSymbolStrategy(symbol),
          side: open.side === 'buy' ? 'long' : 'short',
          buyOrder,
          sellOrder,
          entry_price: open.filled_avg_price,
          exit_price: order.filled_avg_price,
          qty: order.filled_qty,
          pnl,
          pnlPercent,
          entry_time: open.filled_at,
          exit_time: order.filled_at,
          holding_ms: Math.abs(new Date(order.filled_at) - new Date(open.filled_at))
        });
        delete openPositions[symbol];
      } else {
        // Same side again — treat as a new position (re-entry after close)
        openPositions[symbol] = order;
      }
    });

    return trades.sort((a, b) => new Date(b.exit_time) - new Date(a.exit_time));
  };

  const allTrades = groupIntoTrades();

  // Filter trades by selected strategy
  const trades = selectedStrategy === 'all'
    ? allTrades
    : allTrades.filter(t => t.strategy === selectedStrategy);

  // Filter orders by strategy for pending/all views
  const filterOrdersByStrategy = (ordersList) => {
    if (selectedStrategy === 'all') return ordersList;
    const symbols = STRATEGY_SYMBOLS[selectedStrategy];
    return ordersList.filter(o => symbols.includes(o.symbol));
  };

  const pendingOrders = filterOrdersByStrategy(
    orders.filter(o => ['new', 'accepted', 'pending_new', 'accepted_for_bidding', 'partially_filled'].includes(o.status))
  );
  const allOrdersFiltered = filterOrdersByStrategy(orders);

  // Strategy colors
  const strategyColors = {
    vwap: '#00d4ff',
    meanrev: '#00ff88',
    momentum: '#ffd93d',
    unknown: '#888888'
  };

  const formatHoldTime = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${days}d`;
    return `${hours}h`;
  };

  const formatStrategyName = (strategy) => {
    return STRATEGY_NAMES[strategy] || strategy;
  };

  // Pagination helpers
  const paginate = (list) => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  };

  const totalPages = (list) => Math.max(1, Math.ceil(list.length / PAGE_SIZE));

  const activeList = filter === 'filled' ? trades : filter === 'pending' ? pendingOrders : allOrdersFiltered;
  const pages = totalPages(activeList);

  if (loading) {
    return <div className="loading">Loading trade history...</div>;
  }

  return (
    <div className="trade-history">
      <div className="trade-header">
        <h1>Trade History</h1>

        {/* Strategy Selector */}
        <div className="strategy-selector">
          <label>Strategy:</label>
          <select
            value={selectedStrategy}
            onChange={(e) => setSelectedStrategy(e.target.value)}
            className="strategy-dropdown"
          >
            <option value="all">{STRATEGY_NAMES.all}</option>
            <option value="vwap">{STRATEGY_NAMES.vwap}</option>
            <option value="meanrev">{STRATEGY_NAMES.meanrev}</option>
            <option value="momentum">{STRATEGY_NAMES.momentum}</option>
          </select>
        </div>
      </div>

      {/* Filter */}
      <div className="trade-filters">
        <button
          className={filter === 'filled' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('filled')}
        >
          Completed Trades ({trades.length})
        </button>
        <button
          className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('all')}
        >
          All Orders ({allOrdersFiltered.length})
        </button>
        <button
          className={filter === 'pending' ? 'filter-btn active' : 'filter-btn'}
          onClick={() => setFilter('pending')}
        >
          Pending ({pendingOrders.length})
        </button>
      </div>

      {/* Completed Trades View */}
      {filter === 'filled' && (
        trades.length === 0 ? (
          <div className="no-trades">
            <span className="empty-icon">📊</span>
            <p>No completed trades yet</p>
            <span className="empty-subtitle">
              {selectedStrategy === 'all'
                ? 'Trades will appear here after positions are closed'
                : `No ${STRATEGY_NAMES[selectedStrategy]} trades yet`
              }
            </span>
          </div>
        ) : (
          <>
            <div className="trades-table">
              <div className="table-header">
                <div>Symbol</div>
                <div>Strategy</div>
                <div>Entry</div>
                <div>Exit</div>
                <div>Qty</div>
                <div>P&L</div>
                <div>P&L %</div>
                <div>Hold Time</div>
                <div>Exit Date</div>
              </div>
              {paginate(trades).map((trade, idx) => (
                <div key={idx} className="table-row">
                  <div className="symbol">{trade.symbol}</div>
                  <div style={{color: strategyColors[trade.strategy]}}>
                    {formatStrategyName(trade.strategy)}
                  </div>
                  <div>${trade.entry_price.toFixed(2)}</div>
                  <div>${trade.exit_price.toFixed(2)}</div>
                  <div>{trade.qty}</div>
                  <div className={trade.pnl >= 0 ? 'positive' : 'negative'}>
                    {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                  </div>
                  <div className={trade.pnlPercent >= 0 ? 'positive' : 'negative'}>
                    {trade.pnlPercent >= 0 ? '+' : ''}{trade.pnlPercent.toFixed(2)}%
                  </div>
                  <div>{formatHoldTime(trade.holding_ms)}</div>
                  <div className="date-cell">
                    {new Date(trade.exit_time).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
            <Pagination current={currentPage} total={pages} onChange={setCurrentPage} />
          </>
        )
      )}

      {/* Pending Orders View */}
      {filter === 'pending' && (
        pendingOrders.length === 0 ? (
          <div className="no-trades">
            <span className="empty-icon">⏳</span>
            <p>No pending orders</p>
            <span className="empty-subtitle">Orders awaiting execution will appear here</span>
          </div>
        ) : (
          <>
            <div className="orders-table">
              <div className="table-header">
                <div>Symbol</div>
                <div>Strategy</div>
                <div>Side</div>
                <div>Qty</div>
                <div>Type</div>
                <div>Status</div>
                <div>Submitted</div>
              </div>
              {paginate(pendingOrders).map((order, idx) => (
                <div key={idx} className="table-row">
                  <div className="symbol">{order.symbol}</div>
                  <div style={{color: strategyColors[getSymbolStrategy(order.symbol)]}}>
                    {STRATEGY_NAMES[getSymbolStrategy(order.symbol)]}
                  </div>
                  <div className={order.side === 'buy' ? 'positive' : 'negative'}>
                    {order.side.toUpperCase()}
                  </div>
                  <div>{order.qty}</div>
                  <div>{order.type}</div>
                  <div className="status-badge">{order.status}</div>
                  <div className="date-cell">
                    {new Date(order.submitted_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <Pagination current={currentPage} total={pages} onChange={setCurrentPage} />
          </>
        )
      )}

      {/* All Orders View */}
      {filter === 'all' && (
        <>
          <div className="orders-table">
            <div className="table-header">
              <div>Symbol</div>
              <div>Strategy</div>
              <div>Side</div>
              <div>Qty</div>
              <div>Type</div>
              <div>Price</div>
              <div>Status</div>
              <div>Time</div>
            </div>
            {paginate(allOrdersFiltered).map((order, idx) => (
              <div key={idx} className="table-row">
                <div className="symbol">{order.symbol}</div>
                <div style={{color: strategyColors[getSymbolStrategy(order.symbol)]}}>
                  {STRATEGY_NAMES[getSymbolStrategy(order.symbol)]}
                </div>
                <div className={order.side === 'buy' ? 'positive' : 'negative'}>
                  {order.side.toUpperCase()}
                </div>
                <div>{order.filled_qty || order.qty}</div>
                <div>{order.type}</div>
                <div>
                  {order.filled_avg_price ? `$${order.filled_avg_price.toFixed(2)}` : '-'}
                </div>
                <div>
                  <span className={`status-badge status-${order.status}`}>
                    {order.status}
                  </span>
                </div>
                <div className="date-cell">
                  {new Date(order.submitted_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
          <Pagination current={currentPage} total={pages} onChange={setCurrentPage} />
        </>
      )}
    </div>
  );
};

const Pagination = ({ current, total, onChange }) => {
  // Show a window of 5 pages centred on the current page
  const windowSize = 7;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, start + windowSize - 1);
  if (end - start < windowSize - 1) start = Math.max(1, end - windowSize + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
  <div className="pagination">
    <button
      className="page-btn"
      onClick={() => onChange(current - 1)}
      disabled={current === 1}
    >
      ‹
    </button>
    {start > 1 && <span className="page-ellipsis">…</span>}
    {pages.map(page => (
      <button
        key={page}
        className={`page-btn ${page === current ? 'active' : ''}`}
        onClick={() => onChange(page)}
      >
        {page}
      </button>
    ))}
    {end < total && <span className="page-ellipsis">…</span>}
    <button
      className="page-btn"
      onClick={() => onChange(current + 1)}
      disabled={current === total}
    >
      ›
    </button>
  </div>
  );
};

export default TradeHistory;
